import { clsx } from 'clsx'

import { buildStoryboardSnapshotTimes, useMuxStoryboardFrames } from 'utils/muxStoryboard'

import type { ReactElement } from 'react'
import type { MuxStoryboardFrame } from 'utils/muxStoryboard'
import type { MuxPlaybackEmbed } from 'utils/videoUrlResolver'

type MuxStoryboardFrameStripMode = 'even' | 'midpoint'

type MuxStoryboardFrameStripProperties = {
  className?: string
  durationSeconds?: number
  emptyClassName?: string
  fallbackFrameClassName?: string
  fallbackImageClassName?: string
  frameClassName?: string
  frameCount: number
  frameWrapperClassName?: string
  mode: MuxStoryboardFrameStripMode
  mux?: MuxPlaybackEmbed
}

const renderFrame = (
  frame: MuxStoryboardFrame,
  frameClassName: string | undefined,
  frameWrapperClassName: string | undefined,
  index: number
): ReactElement => {
  const image = (
    <svg
      key={`frame-${String(index)}-${String(frame.startTimeSeconds)}`}
      className={frameClassName}
      viewBox={`${frame.x} ${frame.y} ${frame.tileWidth} ${frame.tileHeight}`}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <image href={frame.imageUrl} width={frame.sheetWidth} height={frame.sheetHeight} />
    </svg>
  )

  if (!frameWrapperClassName) {
    return image
  }

  return (
    <div className={frameWrapperClassName} key={`frame-${String(index)}-${String(frame.startTimeSeconds)}`}>
      {image}
    </div>
  )
}

const renderFallbackThumbnail = (
  imageUrl: string,
  imageClassName: string | undefined,
  wrapperClassName: string | undefined,
  wrapperFallbackClassName: string | undefined,
  index: number,
  timeSeconds: number
): ReactElement => {
  const key = `thumbnail-${String(index)}-${String(timeSeconds)}`
  const image = <img key={key} className={imageClassName} src={imageUrl} alt="" />

  if (!wrapperClassName && !wrapperFallbackClassName) {
    return image
  }

  return (
    <div className={clsx(wrapperClassName, wrapperFallbackClassName)} key={key}>
      {image}
    </div>
  )
}

export const MuxStoryboardFrameStrip = ({
  className,
  durationSeconds,
  emptyClassName,
  fallbackFrameClassName,
  fallbackImageClassName,
  frameClassName,
  frameCount,
  frameWrapperClassName,
  mode,
  mux,
}: MuxStoryboardFrameStripProperties) => {
  /* --- Section 1: Hooks --- */
  const frames = useMuxStoryboardFrames({
    durationSeconds,
    frameCount,
    mode,
    mux,
  })

  /* --- Section 3: Derived Values --- */
  const snapshotTimes = buildStoryboardSnapshotTimes(durationSeconds, frameCount, mode)
  const fallbackThumbnailUrl = mux?.thumbnailUrl

  /* --- Section 5: Early Returns --- */
  if (snapshotTimes.length === 0) {
    return <div className={clsx(className, emptyClassName)} aria-hidden />
  }

  /* --- Section 6: Render --- */
  return (
    <div className={className} aria-hidden>
      {frames.length > 0
        ? frames.map((frame, index) => renderFrame(frame, frameClassName, frameWrapperClassName, index))
        : snapshotTimes.map((timeSeconds, index) =>
            fallbackThumbnailUrl
              ? renderFallbackThumbnail(
                  fallbackThumbnailUrl,
                  fallbackImageClassName ?? frameClassName,
                  frameWrapperClassName,
                  fallbackFrameClassName,
                  index,
                  timeSeconds
                )
              : null
          )}
    </div>
  )
}
